import React, { useState, useEffect } from "react";
import {
  Folder as LucideFolder,
  Users,
  Upload,
  Link2,
  File as FileIcon,
  FileText,
  FileImage,
} from "lucide-react";
import { FaLock, FaLockOpen } from "react-icons/fa";
import {
  getFirestore,
  collection,
  query,
  getDocs,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { app } from "../firebase";
import FileUploadArea from "./FileUploadArea";

const db = getFirestore(app);

const PublicFolderUpload = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [folderName, setFolderName] = useState("");
  const [folderId, setFolderId] = useState(null);
  const [folderExists, setFolderExists] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [readonly, setReadonly] = useState(false);
  const [suggestedFolders, setSuggestedFolders] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  /* ------------------------------------------
     Suggest folders based on FIRST NAME
  ------------------------------------------ */
  const handleFirstNameBlur = async () => {
    const trimmedFirst = firstName.trim().toLowerCase();
    if (!trimmedFirst) return;

    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "folderStructures"));
      const folders = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
      }));

      const suggestions = folders
        .filter(f =>
          f.name?.trim().toLowerCase().includes(trimmedFirst)
        )
        .map(f => f.name);

      setSuggestedFolders(suggestions);
      setShowSuggestions(suggestions.length > 0);

      const match = folders.find(f =>
        f.name?.trim().toLowerCase().includes(trimmedFirst)
      );

      if (match) {
        setFolderExists(true);
        setFolderId(match.id);
        setReadonly(true);
        setIsLocked(!!match.locked);

        if (match.locked) {
          setError("🔒 This folder is locked. Please contact administrator.");
          setSuccess("");
        } else {
          setSuccess("Folder already exists. You can upload files.");
          setError("");
        }
      } else {
        setFolderExists(false);
        setFolderId(null);
        setReadonly(false);
        setIsLocked(false);
        setSuccess("");
      }
    } catch (err) {
      setError("Error checking folder: " + err.message);
    }
    setLoading(false);
  };

  /* ------------------------------------------
     CREATE FOLDER
  ------------------------------------------ */
  const createFolder = async () => {
    const trimmedName = folderName.trim();
    if (!trimmedName) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const snap = await getDocs(collection(db, "folderStructures"));
      const existing = snap.docs.find(
        d =>
          d.data().name?.trim().toLowerCase() ===
          trimmedName.toLowerCase()
      );

      if (existing) {
        const data = existing.data();
        setFolderExists(true);
        setFolderId(existing.id);
        setReadonly(true);
        setIsLocked(!!data.locked);

        if (data.locked) {
          setError(
            "🔒 This folder is locked. Please contact administrator."
          );
        } else {
          setSuccess("Folder already exists. You can upload files.");
        }
      } else {
        const now = Timestamp.now();
        const docRef = await addDoc(
          collection(db, "folderStructures"),
          {
            name: trimmedName,
            parentId: null,
            locked: false, // ✅ DEFAULT UNLOCKED
            createdAt: now,
            updatedAt: now,
            modifiedAt: now,
            files: [],
          }
        );

        setFolderExists(true);
        setReadonly(true);
        setFolderId(docRef.id);
        setIsLocked(false);
        setSuccess("Folder created. You can now upload files.");
      }
    } catch (err) {
      setError("Error creating folder: " + err.message);
    }
    setLoading(false);
  };

  /* ------------------------------------------
     DEBOUNCED CHECK + SUGGESTIONS
  ------------------------------------------ */
  useEffect(() => {
    let checkTimer;
    let suggestTimer;

    const combined =
      firstName.trim() && lastName.trim()
        ? `${firstName.trim()} ${lastName.trim()}`
        : "";

    setFolderName(combined);

    if (combined) {
      checkTimer = setTimeout(async () => {
        try {
          const snap = await getDocs(
            collection(db, "folderStructures")
          );
          const match = snap.docs.find(
            d =>
              d.data().name?.trim().toLowerCase() ===
              combined.toLowerCase()
          );

          if (match) {
            const data = match.data();
            setFolderExists(true);
            setFolderId(match.id);
            setReadonly(true);
            setIsLocked(!!data.locked);

            if (data.locked) {
              setError(
                "🔒 This folder is locked. Please contact administrator."
              );
              setSuccess("");
            } else {
              setSuccess(
                "Folder already exists. You can upload files."
              );
              setError("");
            }
          } else {
            setFolderExists(false);
            setFolderId(null);
            setReadonly(false);
            setIsLocked(false);
            setSuccess("");
          }
        } catch {}
      }, 400);

      suggestTimer = setTimeout(async () => {
        const snap = await getDocs(
          collection(db, "folderStructures")
        );
        const search = combined.toLowerCase();
        const matches = snap.docs
          .map(d => d.data().name)
          .filter(
            n =>
              n &&
              n.toLowerCase().includes(search) &&
              n.toLowerCase() !== search
          );

        setSuggestedFolders(matches);
        setShowSuggestions(matches.length > 0);
      }, 1000);
    } else {
      setFolderExists(false);
      setReadonly(false);
      setIsLocked(false);
      setSuggestedFolders([]);
      setShowSuggestions(false);
      setSuccess("");
      setError("");
    }

    return () => {
      clearTimeout(checkTimer);
      clearTimeout(suggestTimer);
    };
  }, [firstName, lastName]);

  /* ------------------------------------------
     FILE TYPE ICON
  ------------------------------------------ */
  const getFileTypeIcon = name => {
    const ext = name.split(".").pop().toLowerCase();
    if (["jpg", "png", "jpeg"].includes(ext))
      return <FileImage size={16} />;
    if (["pdf"].includes(ext)) return <FileIcon size={16} />;
    if (["txt", "md"].includes(ext))
      return <FileText size={16} />;
    return <LucideFolder size={16} />;
  };

  /* ------------------------------------------
     RENDER
  ------------------------------------------ */
  return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#0071bd] to-[#6dd5ed] p-4">
    <div className="max-w-lg w-full bg-white/90 rounded-2xl shadow-2xl p-8 animate-fade-in-up">
        <h2 className="text-2xl font-bold text-center text-[#0071bd] mb-6">Create or Access Your Folder</h2>
        {/* Note Section */}
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded text-yellow-900 text-sm flex flex-col gap-1">
          <span className="font-semibold flex items-center gap-1"><span className="text-lg">&#9888;</span> Please upload the following documents for Job Process:</span>
          <ul className="list-decimal list-inside ml-2">
            <li>Upload <span className="font-semibold text-[#0071bd]">Aadhar Card</span></li>
            <li>Upload <span className="font-semibold text-[#0071bd]">Pan Card</span></li>
            <li>Upload <span className="font-semibold text-[#0071bd]">Passport size photo</span></li>
            <li>Upload <span className="font-semibold text-[#0071bd]">Bank Passbook copy</span></li>
            <li>Upload <span className="font-semibold text-[#0071bd]">Resume</span></li>
           <li>Upload <span className="font-semibold text-[#0071bd]">EPFO Service History if having exp</span></li>
            <li>
              Please edit the sample 
              <a
                href={process.env.PUBLIC_URL ? process.env.PUBLIC_URL + '/assets/Template-PersonalDetails.docx' : '/assets/Template-PersonalDetails.docx'}
                download
                className="font-semibold text-blue-700 underline hover:text-blue-900 mx-1"
                style={{ textDecoration: "underline", fontWeight: "bold", color:"blue" }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Template-PersonalDetails.docx
              </a>
              then re-upload it here.
            </li>
          </ul>
        </div>
        <div className="mb-4 flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0071bd] transition duration-150 ease-in-out"
              placeholder="First Name"
              value={firstName}
              onChange={e => { setFirstName(e.target.value.replace(/[^a-zA-Z ]/g, "")); setReadonly(false); setSuccess(""); setError(""); }}
              onBlur={handleFirstNameBlur}
          
              disabled={loading}
            />
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0071bd] transition duration-150 ease-in-out"
              placeholder="Last Name"
              value={lastName}
              onChange={e => { setLastName(e.target.value.replace(/[^a-zA-Z ]/g, "")); setReadonly(false); setSuccess(""); setError(""); }}
             
              disabled={loading}
            />
            {!readonly && (
              <button
                className={`px-4 py-2 rounded-md transition ${loading || !folderName || folderExists || (suggestedFolders.length > 0) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#0071bd] text-white hover:bg-[#005fa3]'}`}
                onClick={createFolder}
                disabled={loading || !folderName || folderExists || (suggestedFolders.length > 0)}
              >
                Create
              </button>
            )}
          </div>
          {showSuggestions && suggestedFolders.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mt-1">
              <div className="text-xs text-blue-700 mb-1">Similar folders found. You can upload files to these:</div>
              <ul className="flex flex-wrap gap-2">
                {suggestedFolders.map(name => (
                  <li key={name}>
                    <button
                      className="flex items-center gap-1 px-3 py-1 bg-white border border-blue-300 rounded hover:bg-blue-100 transition text-blue-700 text-sm"
                      onClick={async () => { 
                        const [fn, ln] = name.split('_');
                        setFirstName(fn || "");
                        setLastName(ln || "");
                        setFolderName(name);
                        setReadonly(true); 
                        setShowSuggestions(false); 
                        // Find the folderId for the selected suggestion
                        try {
                          const q2 = query(collection(db, "folderStructures"));
                          const snap2 = await getDocs(q2);
                          const match = snap2.docs.find(doc => (doc.data().name || "").trim().toLowerCase() === name.trim().toLowerCase());
                          const matchData = match?.data();
                          if (match && !matchData.locked) {
                            setFolderId(match.id);
                            setFolderExists(true);
                          
                            setSuccess("Folder already exists. You can upload files to it.");
                          } 
                          else if(!!matchData.locked) {
                            setIsLocked(!!matchData.locked);
                              setError("🔒 This folder is locked. Please contact administrator.");
                              setSuccess("");
                          }
                          else {
                            setFolderId(null);
                            setFolderExists(false);
                            setSuccess("");
                          }
                        } catch (err) {
                          setError("Error finding folder: " + err.message);
                        }
                      }}
                    >
                      {getFileTypeIcon(name)}
                      {name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {success && <div className="mb-2 p-2 bg-green-100 text-green-700 rounded">{success}</div>}
        {error && <div className="mb-2 p-2 bg-red-100 text-red-700 rounded">{error}</div>}
       
        {/* ✅ UPLOAD ONLY IF UNLOCKED */}
        {folderExists && folderId && !isLocked && (
          <FileUploadArea
            folderId={folderId}
            folderName={folderName.trim()}
            isLocked={isLocked}
            readonlyFolderName={false}
          />
        )}
      </div>
      </div>
  );
};

export default PublicFolderUpload;
